import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import { services } from '@config/services';
import type {
  CreateSynonymGroupArgs,
  DeleteSynonymGroupArgs,
  DeleteSynonymGroupResponse,
  GetSearchSynonymsResponse,
  ReloadVocabularyResponse,
  SaveSynonymGroupResponse,
  SynonymGroup,
  UpdateSynonymGroupArgs,
} from './searchSynonymsTypes';

/**
 * Pure request builders — exported so the exact wire (URL + method + body) is
 * lockable in a `toEqual` unit test. Endpoints (sku-mapping-service
 * `SearchSynonymsController`):
 *   GET    /sku/v1/searchSynonyms
 *   POST   /sku/v1/searchSynonyms                 body { id?, type, synonyms, input? }
 *   PUT    /sku/v1/searchSynonyms/{groupId}       body { type, synonyms, input? }
 *   DELETE /sku/v1/searchSynonyms/{groupId}
 *   POST   /sku/v1/searchSynonyms/reload          body {}
 */
export const searchSynonymsRequestBuilders = {
  getSearchSynonyms: () => ({
    url: `${services.sku}/v1/searchSynonyms`,
    method: 'GET' as const,
  }),
  createSynonymGroup: ({ id, type, synonyms, input }: CreateSynonymGroupArgs) => ({
    url: `${services.sku}/v1/searchSynonyms`,
    method: 'POST' as const,
    // Omit unset optionals so the body stays byte-identical to a hand-made call.
    body: {
      type,
      synonyms,
      ...(id ? { id } : {}),
      ...(input && input.length > 0 ? { input } : {}),
    },
  }),
  updateSynonymGroup: ({ groupId, type, synonyms, input }: UpdateSynonymGroupArgs) => ({
    url: `${services.sku}/v1/searchSynonyms/${encodeURIComponent(groupId)}`,
    method: 'PUT' as const,
    body: {
      type,
      synonyms,
      ...(input && input.length > 0 ? { input } : {}),
    },
  }),
  deleteSynonymGroup: ({ groupId }: DeleteSynonymGroupArgs) => ({
    url: `${services.sku}/v1/searchSynonyms/${encodeURIComponent(groupId)}`,
    method: 'DELETE' as const,
  }),
  reloadVocabulary: () => ({
    url: `${services.sku}/v1/searchSynonyms/reload`,
    method: 'POST' as const,
    body: {},
  }),
};

/** Tolerate a defensive `{ data: {...} }` wrapper (other portal services ship both). */
function unwrap<T>(response: T | { data?: T } | unknown): T | undefined {
  const r = response as { data?: T };
  if (r && typeof r === 'object' && 'data' in r && r.data != null) return r.data;
  return response as T;
}

/** Unwrap the list envelope; default to an empty vocabulary on an unexpected shape. */
export function unwrapSearchSynonyms(response: unknown): GetSearchSynonymsResponse {
  const d = unwrap<GetSearchSynonymsResponse>(response);
  return {
    items: Array.isArray(d?.items) ? d!.items : [],
    collisions: Array.isArray(d?.collisions) ? d!.collisions : [],
  };
}

/** Unwrap a create/update envelope down to the saved group. */
export function unwrapSavedGroup(response: unknown): SaveSynonymGroupResponse {
  const d = unwrap<SaveSynonymGroupResponse>(response);
  const group = (d?.group ?? {}) as SynonymGroup;
  return {
    group: {
      id: group.id ?? '',
      type: group.type ?? '',
      synonyms: Array.isArray(group.synonyms) ? group.synonyms : [],
      input: Array.isArray(group.input) ? group.input : null,
    },
  };
}

/** Unwrap the delete acknowledgement. */
export function unwrapDeletedGroup(response: unknown): DeleteSynonymGroupResponse {
  const d = unwrap<DeleteSynonymGroupResponse>(response);
  return { id: d?.id ?? '', type: d?.type ?? '' };
}

/** Unwrap the reload cache counts. */
export function unwrapReload(response: unknown): ReloadVocabularyResponse {
  const d = unwrap<ReloadVocabularyResponse>(response);
  return {
    synonymGroups: d?.synonymGroups ?? 0,
    synonymTokens: d?.synonymTokens ?? 0,
    noiseTokens: d?.noiseTokens ?? 0,
    brands: d?.brands ?? 0,
    modelTokens: d?.modelTokens ?? 0,
    typeFamilies: d?.typeFamilies ?? 0,
    typeNouns: d?.typeNouns ?? 0,
  };
}

/**
 * Search Synonyms slice (super-admin surface, flag
 * `omProd722AiCatalogMappingAssistant`). Manages the matching vocabulary that
 * feeds Atlas lexical recall and the reranker. Its own slice (not folded into
 * `productMatchingApi`) because the vocabulary is a separate subsystem with its
 * own lifecycle — and every write already reloads the service-side caches, so
 * the only client cache to invalidate is the list itself.
 */
export const searchSynonymsApi = createApi({
  reducerPath: 'searchSynonymsApi',
  baseQuery: portalBaseQuery,
  tagTypes: ['SearchSynonyms'],
  endpoints: (builder) => ({
    // GET /sku/v1/searchSynonyms — full vocabulary (~150 groups, no paging by design).
    getSearchSynonyms: builder.query<GetSearchSynonymsResponse, void>({
      query: searchSynonymsRequestBuilders.getSearchSynonyms,
      transformResponse: unwrapSearchSynonyms,
      providesTags: [{ type: 'SearchSynonyms', id: 'LIST' }],
    }),
    // POST /sku/v1/searchSynonyms — 409s carry collision/duplicate detail in the message.
    createSynonymGroup: builder.mutation<SaveSynonymGroupResponse, CreateSynonymGroupArgs>({
      query: searchSynonymsRequestBuilders.createSynonymGroup,
      transformResponse: unwrapSavedGroup,
      invalidatesTags: [{ type: 'SearchSynonyms', id: 'LIST' }],
    }),
    // PUT /sku/v1/searchSynonyms/{groupId} — type is immutable server-side.
    updateSynonymGroup: builder.mutation<SaveSynonymGroupResponse, UpdateSynonymGroupArgs>({
      query: searchSynonymsRequestBuilders.updateSynonymGroup,
      transformResponse: unwrapSavedGroup,
      invalidatesTags: [{ type: 'SearchSynonyms', id: 'LIST' }],
    }),
    // DELETE /sku/v1/searchSynonyms/{groupId}
    deleteSynonymGroup: builder.mutation<DeleteSynonymGroupResponse, DeleteSynonymGroupArgs>({
      query: searchSynonymsRequestBuilders.deleteSynonymGroup,
      transformResponse: unwrapDeletedGroup,
      invalidatesTags: [{ type: 'SearchSynonyms', id: 'LIST' }],
    }),
    // POST /sku/v1/searchSynonyms/reload — refresh after mongosh/seed edits; the
    // CRUD endpoints above already reload after every write.
    reloadVocabulary: builder.mutation<ReloadVocabularyResponse, void>({
      query: searchSynonymsRequestBuilders.reloadVocabulary,
      transformResponse: unwrapReload,
      invalidatesTags: [{ type: 'SearchSynonyms', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetSearchSynonymsQuery,
  useCreateSynonymGroupMutation,
  useUpdateSynonymGroupMutation,
  useDeleteSynonymGroupMutation,
  useReloadVocabularyMutation,
} = searchSynonymsApi;
